import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, Note, Popover } from '@repo/design-system';
import type { AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { semantic, space, typeRole } from '@repo/design-tokens';

import { specialName, specialSpan } from './hoursModel';
import { actionProblem, type HoursActionResult } from './useOpeningHoursEditor';
import { usePopoverProblem } from './usePopoverProblem';

/**
 * A special period's *Delete* (spec twin-details-page-hours-special, R6.5), in the card's footer
 * for a period that has not started. A confirm, as *Remove* on a standard week: the standard hours
 * apply again on its dates. Its late problems go through `usePopoverProblem` (R7).
 */

export interface DeleteSpecialProps {
  calendar: AuthoredOpeningHoursVersion;
  onDelete: () => Promise<HoursActionResult>;
  /** Deleted: the step closes the card, selects the Now standard and says so. */
  onDeleted: () => void;
  /** An outcome that arrived after the popover was closed, for the step to show and say. */
  onLateProblem: (sentence: string) => void;
  className?: string;
}

export const DELETE_CHANGED =
  'The hours changed while you were deleting this. Check it and delete it again.';
export const DELETE_UNKNOWN = 'Couldn’t tell whether the special period was deleted.';

const DeleteSpecial: React.FC<DeleteSpecialProps> = ({
  calendar,
  onDelete,
  onDeleted,
  onLateProblem,
  className,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  const { open, show, close, problem, report } = usePopoverProblem(onLateProblem);
  const [sending, setSending] = useState(false);
  const name = specialName(calendar);

  const remove = async () => {
    if (sending) return;
    setSending(true);
    report(null);
    const result = await onDelete();
    setSending(false);
    const sentence = actionProblem(result, DELETE_CHANGED, DELETE_UNKNOWN);
    if (sentence === null) {
      close();
      onDeleted();
    } else {
      report(sentence);
    }
  };

  return (
    <span className={className}>
      <Button
        ref={triggerRef}
        variant="text"
        size="sm"
        tone="destructive"
        iconLeft={<Icon name="trash-can" />}
        aria-label={`Delete ${name}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={show}
      >
        Delete
      </Button>
      <Popover
        id={popoverId}
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        title={`Delete ${name}?`}
        align="start"
        initialFocusRef={cancelRef}
        actions={
          <>
            <Button ref={cancelRef} variant="frosted" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              aria-disabled={sending || undefined}
              loading={sending}
              onClick={() => void remove()}
            >
              Delete
            </Button>
          </>
        }
      >
        <div className="body">
          <p className="sentence">Standard hours apply again on {specialSpan(calendar)}.</p>
          {/* Polite, so the outcome is heard while focus stays on Delete. */}
          <div role="status">
            {problem && (
              <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                {problem}
              </Note>
            )}
          </div>
        </div>
      </Popover>
    </span>
  );
};

export default styled(DeleteSpecial)`
  display: inline-flex;

  .body {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
  }

  .sentence {
    margin: 0;
    ${typeRole.body1};
    color: ${semantic.text.primary};
  }
`;
